import React, { useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Upload, X, CheckCircle2, AlertTriangle, Download, FileSpreadsheet } from 'lucide-react';
import * as XLSX from 'xlsx';
import api from '@/lib/api';

// Phase orders-ux · Bulk invoice upload modal.
// Flow: file → parse → preview API → user confirms → apply via existing
// /seller/orders/bulk-invoice endpoint. Never bypasses seller-scoped auth.

const HEADER_ALIASES = {
  order_number: ['주문번호', 'order_number', 'ordernumber', 'order no', 'order#', 'orderno'],
  courier:      ['택배사', 'courier', '배송사', 'shipping company'],
  tracking_number: ['송장번호', 'tracking_number', 'trackingno', 'tracking no', 'tracking'],
};

const findKey = (headers, targets) => {
  const lower = headers.map((h) => String(h || '').trim().toLowerCase());
  for (const t of targets) {
    const idx = lower.indexOf(t.toLowerCase());
    if (idx >= 0) return headers[idx];
  }
  return null;
};

const parseFile = async (file) => {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: 'array' });
  const sheet = wb.Sheets[wb.SheetNames[0]];
  const json = XLSX.utils.sheet_to_json(sheet, { defval: '' });
  if (!json.length) return { rows: [], warning: '빈 파일입니다.' };
  const headers = Object.keys(json[0]);
  const kOrder = findKey(headers, HEADER_ALIASES.order_number);
  const kCourier = findKey(headers, HEADER_ALIASES.courier);
  const kTracking = findKey(headers, HEADER_ALIASES.tracking_number);
  if (!kOrder || !kCourier || !kTracking) {
    return { rows: [], warning: '컬럼을 찾지 못했습니다. 헤더는 "주문번호, 택배사, 송장번호" 형식이어야 합니다.' };
  }
  const rows = json.map((r) => ({
    order_number: String(r[kOrder] ?? '').trim(),
    courier: String(r[kCourier] ?? '').trim(),
    tracking_number: String(r[kTracking] ?? '').trim(),
  })).filter((r) => r.order_number || r.courier || r.tracking_number);
  return { rows };
};

const downloadSample = () => {
  const rows = [
    { 주문번호: '20260810-AAAAA', 택배사: 'CJ대한통운', 송장번호: '1234567890' },
    { 주문번호: '20260810-BBBBB', 택배사: '한진택배',   송장번호: '9876543210' },
  ];
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, '송장');
  XLSX.writeFile(wb, 'livemoa_bulk_invoice_sample.xlsx');
};

const downloadErrors = (rows) => {
  const errs = rows.filter((r) => !r.is_valid).map((r) => ({
    line: r.line, 주문번호: r.order_number, 택배사: r.courier, 송장번호: r.tracking_number,
    사유: (r.errors || []).map((e) => e.message).join('; '),
  }));
  if (!errs.length) return;
  const ws = XLSX.utils.json_to_sheet(errs);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, '오류');
  XLSX.writeFile(wb, 'livemoa_bulk_invoice_errors.xlsx');
};

const BulkInvoiceUploadModal = ({ open, onClose, onDone, onOpenInvoiceTemplate }) => {
  const [step, setStep] = useState('upload'); // upload | preview | applying | done
  const [previewRows, setPreviewRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const reset = () => {
    setStep('upload'); setPreviewRows([]); setSummary(null); setApplyResult(null);
    setBusy(false);
    if (fileRef.current) fileRef.current.value = '';
  };
  const close = () => { reset(); onClose(); };

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const { rows, warning } = await parseFile(f);
      if (warning) { toast.error(warning); setBusy(false); return; }
      if (rows.length === 0) { toast.error('업로드할 데이터가 없습니다.'); setBusy(false); return; }
      // Call preview API
      const r = await api.post('/seller/orders/bulk-invoice-preview', { rows });
      setPreviewRows(r.data?.rows || []);
      setSummary(r.data?.summary || null);
      setStep('preview');
    } catch (err) {
      toast.error(err?.response?.data?.detail || '검증 실패');
    } finally { setBusy(false); }
  };

  const apply = async () => {
    const valid = previewRows.filter((r) => r.is_valid);
    if (valid.length === 0) { toast.error('등록 가능한 주문이 없습니다.'); return; }
    if (!window.confirm(`${valid.length}건의 송장을 등록하고 배송중으로 변경합니다. 진행하시겠습니까?`)) return;
    setBusy(true);
    setStep('applying');
    try {
      const items = valid.map((r) => ({
        seller_order_id: r.seller_order_id, courier: r.courier, tracking_number: r.tracking_number,
      }));
      const r = await api.post('/seller/orders/bulk-invoice', { items });
      const s = r.data || {};
      setApplyResult({
        success: (s.success_ids || []).length,
        skipped: (s.skipped || []).length,
        warnings: (s.warnings || []).length,
        details: s,
      });
      setStep('done');
      toast.success(`${(s.success_ids || []).length}건 등록 완료`);
      onDone && onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || '등록 실패');
      setStep('preview');
    } finally { setBusy(false); }
  };

  const invalid = useMemo(() => previewRows.filter((r) => !r.is_valid), [previewRows]);
  const valid = useMemo(() => previewRows.filter((r) => r.is_valid), [previewRows]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4"
         onClick={close} data-testid="bulk-invoice-modal">
      <div className="w-full max-w-3xl max-h-[92vh] overflow-y-auto bg-white dark:bg-[#0F0F0F] rounded-t-2xl sm:rounded-2xl shadow-2xl"
           onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-3 border-b border-black/10 dark:border-white/10 bg-white/95 dark:bg-[#0F0F0F]/95 backdrop-blur">
          <p className="text-sm font-black inline-flex items-center gap-2">
            <FileSpreadsheet size={14} className="text-[#FF3B30]" /> 송장 대량등록
          </p>
          <button onClick={close} data-testid="bulk-invoice-close" className="p-1"><X size={16} /></button>
        </div>

        {step === 'upload' && (
          <div className="p-6 space-y-4" data-testid="bulk-invoice-step-upload">
            <div className="rounded-xl border-2 border-dashed border-black/10 dark:border-white/15 p-8 text-center">
              <Upload size={22} className="mx-auto text-gray-400" />
              <p className="text-sm font-bold mt-2">CSV 또는 Excel 파일을 선택하세요</p>
              <p className="text-[11px] text-gray-500 mt-1">헤더: <b>주문번호</b> · <b>택배사</b> · <b>송장번호</b> (최대 2000행)</p>
              <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" onChange={onPickFile}
                     className="hidden" data-testid="bulk-invoice-file-input" id="bulk-invoice-file" />
              <label htmlFor="bulk-invoice-file"
                     className="mt-4 inline-flex items-center gap-1 px-4 py-2 rounded-full bg-[#FF3B30] text-white text-xs font-bold cursor-pointer"
                     data-testid="bulk-invoice-file-pick">
                {busy ? '검증 중…' : '파일 선택'}
              </label>
            </div>
            <div className="text-xs text-gray-500 space-y-1">
              <p>💡 예시 형식:</p>
              <pre className="bg-black/5 dark:bg-white/5 rounded p-2 text-[10px] font-mono">주문번호,택배사,송장번호
20260810-AAAAA,CJ대한통운,1234567890
20260810-BBBBB,한진택배,9876543210</pre>
              <div className="flex flex-wrap gap-2 pt-1">
                <button onClick={downloadSample} className="inline-flex items-center gap-1 text-[#FF3B30] hover:underline font-bold" data-testid="bulk-invoice-sample-download">
                  <Download size={11} /> 샘플 파일 다운로드
                </button>
                {onOpenInvoiceTemplate && (
                  <button onClick={onOpenInvoiceTemplate}
                          className="inline-flex items-center gap-1 text-black dark:text-white hover:underline font-bold"
                          data-testid="bulk-invoice-open-template">
                    <Download size={11} /> 송장 등록용 주문파일 다운로드 (현재 필터)
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {step === 'preview' && (
          <div className="p-4 space-y-3" data-testid="bulk-invoice-step-preview">
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-xl border border-black/10 dark:border-white/10 p-3 text-center">
                <p className="text-[10px] font-black text-gray-400 uppercase">전체</p>
                <p className="text-2xl font-black mt-1" data-testid="bulk-invoice-count-total">{summary?.total ?? 0}</p>
              </div>
              <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/5 p-3 text-center">
                <p className="text-[10px] font-black text-emerald-600 uppercase">정상</p>
                <p className="text-2xl font-black mt-1 text-emerald-600" data-testid="bulk-invoice-count-valid">{summary?.valid ?? 0}</p>
              </div>
              <div className="rounded-xl border border-red-500/40 bg-red-500/5 p-3 text-center">
                <p className="text-[10px] font-black text-red-500 uppercase">오류</p>
                <p className="text-2xl font-black mt-1 text-red-500" data-testid="bulk-invoice-count-invalid">{summary?.invalid ?? 0}</p>
              </div>
            </div>

            {invalid.length > 0 && (
              <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-black text-red-600 inline-flex items-center gap-1">
                    <AlertTriangle size={12} /> 오류 {invalid.length}건
                  </p>
                  <button onClick={() => downloadErrors(previewRows)}
                          className="text-[11px] font-bold text-red-600 hover:underline inline-flex items-center gap-1"
                          data-testid="bulk-invoice-download-errors">
                    <Download size={11} /> 오류 CSV 다운로드
                  </button>
                </div>
                <div className="max-h-40 overflow-y-auto text-[11px]" data-testid="bulk-invoice-error-list">
                  {invalid.slice(0, 200).map((r) => (
                    <div key={`e-${r.line}`} className="border-b border-black/5 dark:border-white/10 py-1">
                      <span className="font-mono text-gray-500">L{r.line}</span> · <b>{r.order_number || '(빈)'} </b>
                      → <span className="text-red-600">{r.errors.map((e) => e.message).join(' · ')}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="rounded-xl border border-black/10 dark:border-white/10 overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-black/5 dark:bg-white/5">
                  <tr className="text-left">
                    <th className="p-2">#</th>
                    <th className="p-2">주문번호</th>
                    <th className="p-2">택배사</th>
                    <th className="p-2">송장번호</th>
                    <th className="p-2">현재 상태</th>
                    <th className="p-2">검증</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-black/5 dark:divide-white/10">
                  {previewRows.slice(0, 200).map((r) => (
                    <tr key={r.line} className={r.is_valid ? '' : 'bg-red-500/5'}>
                      <td className="p-2 font-mono text-gray-400">{r.line}</td>
                      <td className="p-2 font-mono">{r.order_number || '-'}</td>
                      <td className="p-2">{r.courier || '-'}</td>
                      <td className="p-2 font-mono">{r.tracking_number || '-'}</td>
                      <td className="p-2">{r.current_status || '-'}</td>
                      <td className="p-2">
                        {r.is_valid ? (
                          <span className="inline-flex items-center gap-1 text-emerald-600 font-bold"><CheckCircle2 size={11} /> 정상</span>
                        ) : (
                          <span className="text-red-500 text-[10px]">{r.errors[0]?.message || '오류'}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {previewRows.length > 200 && (
              <p className="text-[10px] text-gray-400 text-center">상위 200행만 미리보기로 표시됩니다.</p>
            )}
            <div className="flex gap-2 pt-2">
              <button onClick={reset} className="flex-1 py-2 rounded-full border border-black/10 dark:border-white/10 text-xs font-bold"
                      data-testid="bulk-invoice-reset">파일 다시 선택</button>
              <button onClick={apply} disabled={busy || valid.length === 0}
                      className="flex-2 flex-1 py-2 rounded-full bg-[#FF3B30] text-white text-xs font-bold disabled:opacity-40"
                      data-testid="bulk-invoice-apply">
                {busy ? '등록 중…' : `${valid.length}건 등록`}
              </button>
            </div>
          </div>
        )}

        {step === 'applying' && (
          <div className="p-14 text-center" data-testid="bulk-invoice-step-applying">
            <p className="text-xs text-gray-500">송장을 등록 중입니다…</p>
          </div>
        )}

        {step === 'done' && applyResult && (
          <div className="p-6 space-y-3" data-testid="bulk-invoice-step-done">
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/5 p-3 text-center">
                <p className="text-[10px] font-black text-emerald-600 uppercase">성공</p>
                <p className="text-2xl font-black mt-1 text-emerald-600" data-testid="bulk-invoice-result-success">{applyResult.success}</p>
              </div>
              <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-3 text-center">
                <p className="text-[10px] font-black text-amber-600 uppercase">경고</p>
                <p className="text-2xl font-black mt-1 text-amber-600" data-testid="bulk-invoice-result-warnings">{applyResult.warnings}</p>
              </div>
              <div className="rounded-xl border border-red-500/40 bg-red-500/5 p-3 text-center">
                <p className="text-[10px] font-black text-red-600 uppercase">스킵</p>
                <p className="text-2xl font-black mt-1 text-red-600" data-testid="bulk-invoice-result-skipped">{applyResult.skipped}</p>
              </div>
            </div>
            {(applyResult.details?.skipped || []).length > 0 && (
              <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-3 text-[11px] space-y-1 max-h-40 overflow-y-auto">
                {(applyResult.details.skipped).map((s, i) => (
                  <div key={i}><b>{s.id}</b> · <span className="text-red-500">{s.reason}</span></div>
                ))}
              </div>
            )}
            <button onClick={close} className="w-full py-2 rounded-full bg-black text-white text-xs font-bold"
                    data-testid="bulk-invoice-done-close">닫기</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkInvoiceUploadModal;
